import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import socketService from '../services/socketService'; 
import Layout from '../components/Layout';

const JobTrackerDashboard = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // লোকাল স্টোরেজ থেকে ইউজারের রোল এবং ডিটেইলস নেওয়া হচ্ছে
    const isMaster = localStorage.getItem('isMaster') === 'true'; 
    const loggedInUsername = localStorage.getItem('adminUsername') || localStorage.getItem('username'); 
    const loggedInAdminId = localStorage.getItem('adminId'); 

    const [filters, setFilters] = useState({
        username: '',
        deviceName: '',
        status: '',
        startDate: '',
        endDate: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        totalDocs: 0,
        totalPages: 0
    });

    const fetchJobs = useCallback((page = 1) => {
        setLoading(true);

        const isMasterStatus = localStorage.getItem('isMaster');
        
        // 🛠️ FIX 1: JSON parse error এড়ানোর জন্য সেফটি অ্যাড করা হলো
        let allowedUsersList = [];
        try {
            const storedUsers = localStorage.getItem('allowedUsers');
            if (storedUsers && storedUsers !== 'undefined' && storedUsers !== 'null') {
                allowedUsersList = JSON.parse(storedUsers);
            }
        } catch (error) {
            console.error("Allowed users parse error:", error);
            allowedUsersList = [];
        }

        // 🛠️ FIX 2: সাব-অ্যাডমিন বা সাধারণ ইউজার সবার জন্যই সঠিক ইউজারনেম নেওয়া
        const currentLoggedUser = localStorage.getItem('adminUsername') || localStorage.getItem('username');

        const filterData = {
            page,
            limit: pagination.limit,
            isMaster: isMasterStatus, 
            allowedUsers: allowedUsersList, 
            currentUsername: currentLoggedUser, 
            username: filters.username,
            deviceName: filters.deviceName,
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        // 🛠️ FIX 3: সকেট দিয়ে ডাটা রিকোয়েস্ট পাঠানো এবং সেফটি ব্লক যুক্ত করা
        socketService.emit('get_job_tracking_data', filterData, (response) => {
            console.log("📥 Data received from backend:", response); 
            
            try {
                if (response && response.status === 'success') {
                    setJobs(response.data || []);
                    if (response.pagination) {
                        setPagination(prev => ({
                            ...prev,
                            page: response.pagination.currentPage || 1,
                            totalDocs: response.pagination.totalDocs || 0,
                            totalPages: response.pagination.totalPages || 1
                        }));
                    }
                } else {
                    console.error("Failed to fetch jobs:", response?.message);
                    setJobs([]);
                }
            } catch (err) {
                console.error("Error processing frontend data:", err);
                setJobs([]);
            } finally {
                setLoading(false); // রেসপন্স যাই আসুক বা এরর খাক, লোডিং অফ হবেই
            }
        });
    }, [filters, pagination.limit]);

    
    // প্রাথমিক লোড
    useEffect(() => {
        // সকেট কানেক্ট করা
        socketService.connect();
        fetchJobs(1);
    }, [fetchJobs]);

    // রিয়েল-টাইম লাইভ আপডেট রিসিভ করা
    useEffect(() => {
        const handleJobUpdate = (updatedJob) => {
            // সাধারণ ইউজার হলে অন্য কারো রিয়েল-টাইম আপডেট যেন না দেখে সেটা চেক করা
            if (!isMaster && updatedJob.username !== loggedInUsername) {
                return; 
            }

            setJobs(prevJobs => {
                const existingIndex = prevJobs.findIndex(job => job.jobId === updatedJob.jobId);
                if (existingIndex !== -1) {
                    const newJobs = [...prevJobs];
                    newJobs[existingIndex] = updatedJob;
                    return newJobs;
                } else {
                    return [updatedJob, ...prevJobs].slice(0, pagination.limit);
                }
            });
        };

        socketService.on('admin_job_tracking_update', handleJobUpdate);

        return () => {
            socketService.off('admin_job_tracking_update', handleJobUpdate);
        };
    }, [pagination.limit, isMaster, loggedInUsername]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => fetchJobs(1);
    
    const resetFilters = () => {
        setFilters({ username: '', deviceName: '', status: '', startDate: '', endDate: '' });
        setTimeout(() => fetchJobs(1), 100);
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'COMPLETED': return <span className="badge-seen" style={{ color: '#16a34a', background: '#f0fdf4', borderColor: '#bbf7d0' }}>COMPLETED</span>;
            case 'FAILED': return <span className="badge-unseen" style={{ color: '#dc2626', background: '#fef2f2', borderColor: '#fecaca' }}>FAILED</span>;
            case 'IN_PROGRESS':
            case 'STARTED': return <span className="badge-seen" style={{ color: '#2563eb', background: '#eff6ff', borderColor: '#bfdbfe' }}>{status}</span>;
            case 'TIMEOUT': return <span className="badge-unseen" style={{ color: '#ca8a04', background: '#fefce8', borderColor: '#fef08a' }}>TIMEOUT</span>;
            default: return <span className="badge-seen" style={{ color: '#475569', background: '#f8fafc', borderColor: '#e2e8f0' }}>{status}</span>;
        }
    };

    return (
        <Layout>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; padding: 20px;}
                .section-title { color: #0d6efd; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0d6efd; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.02);}
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,0.1); }
                .btn-apply { background: #0d6efd; color: white; border-radius: 8px; font-size: 13px; border: none; transition: 0.3s; padding: 8px 16px; cursor: pointer;}
                .btn-apply:hover { background: #0a58ca; }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; transition: 0.3s; padding: 8px 16px; cursor: pointer; margin-left: 10px;}
                .btn-reset:hover { background: #e2e8f0; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.02);}
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #f1f5f9; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #e2e8f0; text-align: left;}
                .custom-zebra-table tbody tr:hover { background-color: #f1f5f9; transition: 0.2s; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0d6efd; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #64748b; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 2px 6px; border-radius: 4px; margin-top: 3px; }
                .job-id-tag { color: #6366f1; font-weight: 700; font-size: 11px; background: #e0e7ff; padding: 2px 6px; border-radius: 4px;}
                .msg-body { color: #334155; font-size: 12px; line-height: 1.4; max-width: 250px;}
                .date-bold { color: #334155; font-weight: 700; font-size: 12px; }
                .time-muted { font-size: 11px; color: #64748b; }
                .badge-seen { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; border: 1px solid; display: inline-block; }
                .badge-unseen { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; border: 1px solid; display: inline-block; }
                .progress-bar-bg { width: 100px; height: 6px; background: #e2e8f0; border-radius: 10px; margin-top: 5px; overflow: hidden; }
                .progress-bar-fill { height: 100%; background: #0d6efd; transition: width 0.3s ease; }
            `}</style>

            <div className="main-content-area">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="section-title m-0">⚡ Live Job Tracker</h4>
                    <div className="count-badge">
                        Total Records: <span>{pagination.totalDocs}</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="filter-card mb-4">
                    <div className="row g-3">
                        {/* মাস্টার হলেই শুধুমাত্র Username ফিল্টার দেখতে পারবে */}
                        {isMaster && (
                            <div className="col-md-2">
                                <div className="field-label">Username</div>
                                <input type="text" className="modern-input" name="username" value={filters.username} onChange={handleFilterChange} placeholder="Enter username" />
                            </div>
                        )}
                        <div className="col-md-2">
                            <div className="field-label">Device Name</div>
                            <input type="text" className="modern-input" name="deviceName" value={filters.deviceName} onChange={handleFilterChange} placeholder="Enter device" />
                        </div>
                        <div className="col-md-2">
                            <div className="field-label">Status</div>
                            <select className="modern-input" name="status" value={filters.status} onChange={handleFilterChange}>
                                <option value="">All Status</option>
                                <option value="QUEUED">QUEUED</option>
                                <option value="STARTED">STARTED</option>
                                <option value="IN_PROGRESS">IN_PROGRESS</option>
                                <option value="COMPLETED">COMPLETED</option>
                                <option value="FAILED">FAILED</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <div className="field-label">Start Date</div>
                            <input type="date" className="modern-input" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <div className="field-label">End Date</div>
                            <input type="date" className="modern-input" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn-apply w-100 mb-1" onClick={applyFilters}>Filter</button>
                            <button className="btn-reset w-100 mb-1" onClick={resetFilters}>Reset</button>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="table-container">
                    <table className="custom-zebra-table">
                        <thead>
                            <tr>
                                <th>User & Device</th>
                                <th>Job Details</th>
                                <th>Status & Progress</th>
                                <th>Message</th>
                                <th>Last Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && jobs.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-4">Loading data...</td></tr>
                            ) : jobs.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">No jobs found</td></tr>
                            ) : (
                                jobs.map((job) => {
                                    const progressPercent = job.totalSteps > 0 
                                        ? Math.min(100, Math.round((job.completedSteps / job.totalSteps) * 100)) 
                                        : 0;
                                        
                                    return (
                                        <tr key={job._id || job.jobId}>
                                            <td>
                                                <div className="user-primary">{job.username}</div>
                                                <div className="device-secondary">{job.deviceName}</div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{job.jobType}</div>
                                                <div className="job-id-tag mt-1">ID: {job.jobId.substring(0, 8)}...</div>
                                            </td>
                                            <td>
                                                {getStatusBadge(job.jobStatus)}
                                                <div className="mt-2" style={{ fontSize: '10px', fontWeight: 600, color: '#64748b' }}>
                                                    Step: {job.completedSteps} / {job.totalSteps} ({progressPercent}%)
                                                </div>
                                                <div className="progress-bar-bg">
                                                    <div className="progress-bar-fill" style={{ width: `${progressPercent}%`, backgroundColor: job.jobStatus === 'FAILED' ? '#dc2626' : job.jobStatus === 'COMPLETED' ? '#16a34a' : '#0d6efd' }}></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="msg-body">{job.message || 'No message'}</div>
                                            </td>
                                            <td>
                                                <div className="date-bold">
                                                    {moment(job.updatedAt || job.deviceTimestamp).tz('Asia/Dhaka').format('DD MMM YYYY')}
                                                </div>
                                                <div className="time-muted">
                                                    {moment(job.updatedAt || job.deviceTimestamp).tz('Asia/Dhaka').format('hh:mm:ss A')}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Simple Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-end mt-3 mb-3 pr-3">
                        <button 
                            className="btn-reset mx-1" 
                            disabled={pagination.page === 1} 
                            onClick={() => fetchJobs(pagination.page - 1)}
                        >
                            Previous
                        </button>
                        <span className="mx-3 align-self-center" style={{ fontSize: '13px', fontWeight: 600 }}>
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button 
                            className="btn-reset mx-1" 
                            disabled={pagination.page === pagination.totalPages} 
                            onClick={() => fetchJobs(pagination.page + 1)}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default JobTrackerDashboard;